import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { useSubscription } from './SubscriptionContext';
import { auth } from '../config/firebase';

const CollectionsContext = createContext();

// Session storage key for collections cache
const COLLECTIONS_CACHE_KEY = 'vibary_collections_cache';

// Get cached collections from sessionStorage
const getCachedCollections = () => {
  try {
    const cached = sessionStorage.getItem(COLLECTIONS_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch {
    // Ignore errors
  }
  return null;
};

// Save collections to sessionStorage cache
const setCachedCollections = (data) => {
  try {
    if (data && data.length > 0) {
      sessionStorage.setItem(COLLECTIONS_CACHE_KEY, JSON.stringify(data));
    } else {
      sessionStorage.removeItem(COLLECTIONS_CACHE_KEY);
    }
  } catch (error) {
    // Storage quota exceeded - clear and don't cache
    if (error.name === 'QuotaExceededError') {
      try {
        sessionStorage.removeItem(COLLECTIONS_CACHE_KEY);
      } catch {
        // Ignore cleanup errors
      }
    }
  }
};

// Clear collections cache (used on logout or user change)
const clearCollectionsCache = () => {
  try {
    sessionStorage.removeItem(COLLECTIONS_CACHE_KEY);
  } catch {
    // Ignore errors
  }
};

export function useCollections() {
  const context = useContext(CollectionsContext);
  if (!context) {
    throw new Error('useCollections must be used within a CollectionsProvider');
  }
  return context;
}

export function CollectionsProvider({ children }) {
  // Initialize from cache for instant render on page refresh
  const [collections, setCollections] = useState(() => getCachedCollections() || []);
  const cachedCollections = getCachedCollections();
  const [loading, setLoading] = useState(!cachedCollections);
  const { user } = useAuth();
  const { isPremium } = useSubscription();
  const lastUserIdRef = useRef(null);
  const [firebaseReady, setFirebaseReady] = useState(!!auth.currentUser);

  // Listen for Firebase auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setFirebaseReady(!!firebaseUser);
    });
    return () => unsubscribe();
  }, []);

  // Fetch collections when user changes AND Firebase auth is ready
  useEffect(() => {
    if (user && (user.userId || user.id) && firebaseReady) {
      const userId = user.userId || user.id;

      // If user changed, clear old cache
      if (lastUserIdRef.current && lastUserIdRef.current !== userId) {
        clearCollectionsCache();
        setCollections([]);
      }
      lastUserIdRef.current = userId;

      // Only fetch if user is premium
      if (isPremium) {
        fetchCollections();
      } else {
        // Clear collections for non-premium users
        setCollections([]);
        setCachedCollections([]);
        setLoading(false);
      }
    } else if (!user) {
      clearCollectionsCache();
      setCollections([]);
      setLoading(false);
    }
  }, [user, firebaseReady, isPremium]);

  // Fetch all collections
  const fetchCollections = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getMyCollections();
      if (response.success && response.data) {
        setCollections(response.data);
        setCachedCollections(response.data);
      }
    } catch (error) {
      // If 403, user doesn't have premium access - silently ignore
      if (error.status !== 403) {
        console.error('Error fetching collections:', error);
      }
      setCollections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new collection
  const createCollection = useCallback(async (data) => {
    try {
      const response = await api.createCollection(data);
      if (response.success && response.data) {
        const newCollection = response.data;
        setCollections(prev => {
          const updated = [...prev, newCollection];
          setCachedCollections(updated);
          return updated;
        });
        return { success: true, data: newCollection };
      }
      return { success: false, error: response.error || 'Failed to create collection' };
    } catch (error) {
      console.error('Error creating collection:', error);
      return { success: false, error: error.message || 'Failed to create collection' };
    }
  }, []);

  // Update a collection
  const updateCollection = useCallback(async (collectionId, updates) => {
    // Optimistically update local state first for instant UI feedback
    setCollections(prev => {
      const updated = prev.map(c =>
        c.id === collectionId ? { ...c, ...updates } : c
      );
      setCachedCollections(updated);
      return updated;
    });

    try {
      const response = await api.updateCollection(collectionId, updates);
      if (response.success) {
        // Don't overwrite with server response - our optimistic update is correct
        // The server response might not include the updated listIds immediately
        return { success: true, data: { id: collectionId, ...updates } };
      }
      // If API failed, we should revert - but for now just log the error
      // The optimistic update will persist until next full refresh
      console.error('Collection update API returned failure:', response.error);
      return { success: false, error: response.error || 'Failed to update collection' };
    } catch (error) {
      console.error('Error updating collection:', error);
      return { success: false, error: error.message || 'Failed to update collection' };
    }
  }, []);

  // Delete a collection
  const deleteCollection = useCallback(async (collectionId) => {
    try {
      const response = await api.deleteCollection(collectionId);
      if (response.success) {
        setCollections(prev => {
          const updated = prev.filter(c => c.id !== collectionId);
          setCachedCollections(updated);
          return updated;
        });
        return { success: true };
      }
      return { success: false, error: response.error || 'Failed to delete collection' };
    } catch (error) {
      console.error('Error deleting collection:', error);
      return { success: false, error: error.message || 'Failed to delete collection' };
    }
  }, []);

  // Reorder collections
  const reorderCollections = useCallback(async (orderedIds) => {
    // Optimistic update
    setCollections(prev => {
      const ordered = orderedIds
        .map(id => prev.find(c => c.id === id))
        .filter(Boolean)
        .map((c, index) => ({ ...c, position: index }));
      setCachedCollections(ordered);
      return ordered;
    });

    try {
      const response = await api.reorderCollections(orderedIds);
      if (!response.success) {
        // Revert on failure
        fetchCollections();
        return { success: false, error: response.error };
      }
      return { success: true };
    } catch (error) {
      console.error('Error reordering collections:', error);
      // Revert on failure
      fetchCollections();
      return { success: false, error: error.message };
    }
  }, [fetchCollections]);

  // Add a list to collections
  const addListToCollections = useCallback(async (listId, collectionIds) => {
    try {
      const response = await api.addListToCollections(listId, collectionIds);
      if (response.success) {
        // Update local state
        setCollections(prev => {
          const updated = prev.map(c => {
            if (collectionIds.includes(c.id) && !c.listIds?.includes(listId)) {
              return { ...c, listIds: [...(c.listIds || []), listId] };
            }
            return c;
          });
          setCachedCollections(updated);
          return updated;
        });
        return { success: true };
      }
      return { success: false, error: response.error };
    } catch (error) {
      console.error('Error adding list to collections:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // Remove a list from a collection
  const removeListFromCollection = useCallback(async (collectionId, listId) => {
    try {
      const response = await api.removeListFromCollection(collectionId, listId);
      if (response.success) {
        // Update local state
        setCollections(prev => {
          const updated = prev.map(c => {
            if (c.id === collectionId) {
              return { ...c, listIds: (c.listIds || []).filter(id => id !== listId) };
            }
            return c;
          });
          setCachedCollections(updated);
          return updated;
        });
        return { success: true };
      }
      return { success: false, error: response.error };
    } catch (error) {
      console.error('Error removing list from collection:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // Get collections for a specific list
  const getCollectionsForList = useCallback((listId) => {
    return collections.filter(c => c.listIds?.includes(listId));
  }, [collections]);

  // Get collection by ID
  const getCollectionById = useCallback((collectionId) => {
    return collections.find(c => c.id === collectionId);
  }, [collections]);

  // Build a map of listId -> collectionIds for quick lookup
  const listToCollectionsMap = useMemo(() => {
    const map = {};
    collections.forEach(collection => {
      (collection.listIds || []).forEach(listId => {
        if (!map[listId]) {
          map[listId] = [];
        }
        map[listId].push(collection.id);
      });
    });
    return map;
  }, [collections]);

  const value = {
    collections,
    loading,
    fetchCollections,
    createCollection,
    updateCollection,
    deleteCollection,
    reorderCollections,
    addListToCollections,
    removeListFromCollection,
    getCollectionsForList,
    getCollectionById,
    listToCollectionsMap,
  };

  return (
    <CollectionsContext.Provider value={value}>
      {children}
    </CollectionsContext.Provider>
  );
}
